import { NetworkError, fetchPublicAPI } from "hooks/usePublicAPI";

import type { SubmitAnswer, SubmitBody } from "./submit";

/** POSTs the submission and reports how the server answered. A signed-in
 * visitor's Firebase ID token rides as the bearer, so the server takes the
 * submission as theirs. The browser never sees a quote token: after-api
 * sets the quote cookie itself. */
export async function postSubmission(
  body: SubmitBody,
  idempotencyKey: string,
  idToken: string | null = null,
): Promise<SubmitAnswer> {
  try {
    const response = await fetchPublicAPI<unknown>("/leads/submissions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Idempotency-Key": idempotencyKey,
        ...(idToken === null ? {} : { Authorization: `Bearer ${idToken}` }),
      },
      body: JSON.stringify(body),
    });
    return { status: 200, body: response };
  } catch (err) {
    if (err instanceof NetworkError) {
      return { status: err.statusCode, body: err.responseData };
    }
    return { status: null, body: undefined };
  }
}

/** A fresh idempotency key: a version 4 UUID. */
export function newIdempotencyKey(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10).join(""),
  ].join("-");
}
